import { useCallback } from "react";

import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { ClientId } from "@fartherfinance/frontend/api/Types";

import useImpersonateClient from "@src/multiCustodian/hooks/Advisor/useImpersonateClient";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import { State } from "@src/store";

interface Bookmark {
  user?: {
    id_user: ClientId;
    email: string;
  };
  label?: string;
  pathname: string;
  search?: string;
  hash?: string;
  state?: any;
  timestamp: Date;
}

interface Location {
  pathname: string;
  search?: string;
  hash?: string;
}

/**
 * A custom React hook that tracks and saves the current location to local storage
 * and provides methods to save, retrieve, and navigate to saved locations.
 *
 * @param {string} STORAGE_KEY - The key used to store the location in localStorage (default: 'savedLocation')
 * @returns {Object} An object containing utility methods for managing saved locations
 */
const useBookmark = () => {
  const navigate = useNavigate();
  const location = useLocation<Bookmark>();
  const applicationMode = useApplicationMode();
  const user = useSelector((state: State) => state.main_Reducer.user);
  const impersonateClient = useImpersonateClient();

  const saveCurrentLocation = useCallback(
    (label?: string) => {
      const state: Bookmark = {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
        timestamp: new Date(),
        user: {
          id_user: ClientId.parse(user.id_user),
          email: user.email || "",
        },
        label,
      };
      navigate(location, { replace: true, state });
    },
    [location, user.id_user, user.email, navigate]
  );

  const saveLocationAndNavigate = useCallback(
    (to: Location, label?: string) => {
      const bookmark = {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
        timestamp: new Date(),
        user: {
          id_user: ClientId.parse(user.id_user),
          email: user.email || "",
        },
        label,
      };
      const navigatingToAdvisorRoute = to.pathname.startsWith("/Advisor");
      if (
        user.isAdvisor &&
        applicationMode.isImpersonationMode &&
        navigatingToAdvisorRoute
      ) {
        impersonateClient(null);
      }
      navigate(to, { state: bookmark });
    },
    [
      applicationMode.isImpersonationMode,
      navigate,
      impersonateClient,
      location.hash,
      location.pathname,
      location.search,
      location.state,
      user.email,
      user.id_user,
      user.isAdvisor,
    ]
  );

  const getSavedLocation: () => Bookmark | null = useCallback(() => {
    const state = location.state;

    // Return null if there's no state or no pathname (indicating no valid bookmark)
    if (!state?.pathname) {
      return null;
    }

    // Only include user data if we have both id_user and email
    const user =
      state?.user?.id_user && state?.user?.email
        ? {
            id_user: state.user.id_user,
            email: state.user.email,
          }
        : undefined;

    return {
      user,
      label: state?.label,
      pathname: state?.pathname,
      search: state?.search,
      hash: state?.hash,
      state: state?.state,
      timestamp: state?.timestamp,
    };
  }, [location.state]);

  const clearSavedLocation = useCallback(() => {
    navigate(location, { replace: true, state: undefined });
  }, [navigate, location]);

  const navigateToSavedLocation = useCallback(() => {
    const savedLocation = getSavedLocation();

    if (savedLocation) {
      if (savedLocation.user) {
        impersonateClient(
          savedLocation.user.id_user as ClientId,
          savedLocation.user.email,
          { first: "", last: "" } // aliased name is not provided in global state, does not seem to affect impersonation
        );
      }
      navigate(
        {
          pathname: savedLocation.pathname,
          search: savedLocation.search,
          hash: savedLocation.hash,
        },
        { state: savedLocation.state }
      );
      return true;
    }

    return false;
  }, [getSavedLocation, navigate, impersonateClient]);

  return {
    saveCurrentLocation,
    saveLocationAndNavigate,
    getSavedLocation,
    navigateToSavedLocation,
    clearSavedLocation,
  };
};

export default useBookmark;
